import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { InvalidateFn } from "../../common/hooks/api";
import { useDataRequest } from "../../common/hooks/api";
import { useSelf } from "../../common/hooks/useSelf";
import { Organization } from "../../common/models";
import { NetworkRequestState } from "../../common/models/NetworkRequest";
import { Api } from "../../common/service/Api";
import { McpTokenDto } from "../../common/service/server/dto/McpTokenDto";
import { OrganizationDto } from "../../common/service/server/dto/OrganizationDto";
import { OrganizationMailboxDto } from "../../common/service/server/dto/OrganizationMailboxDto";
import { PublicDomainSetupInstructionsDto } from "../../common/service/server/dto/PublicDomainSetupInstructionsDto";
import { PublicOrganizationNoticeDto } from "../../common/service/server/dto/PublicOrganizationNoticeDto";
import { OrganizationPublicId } from "../../common/service/server/Types";
import { Error as RequestsError } from "../copy/requests";
import { OrganizationUserDto } from "../../common/service/server/dto/UserDto";
import { useOrganizationUsers } from "./useOrganizationUsers";

//
// Organization request
//
const orgQueryKey = (orgId: string) => ["organization", orgId];
export const useOrganization = (
  organizationId: string | null,
  onlyUseSettled = false,
): [Organization | undefined, NetworkRequestState, () => Promise<void>, InvalidateFn] => {
  const apiRequest = useCallback(
    () => Api.organization.getOrganization(organizationId),
    [organizationId],
  );

  const {
    result: organization,
    request,
    refresh: fetch,
    invalidate,
    isSettled,
  } = useDataRequest({
    queryKey: organizationId ? orgQueryKey(organizationId) : [],
    api: apiRequest,
    messages: { defaultError: RequestsError.GetOrg },
    notReady: !organizationId,
  });

  let result = organization;
  if (onlyUseSettled && !isSettled) {
    result = undefined;
  }

  return [result, request, fetch, invalidate];
};

export const useUpdateOrganizationFinancialIncentives = (orgId: string) => {
  const qc = useQueryClient();
  const mutation = useMutation(
    async ({ incentive }: { incentive: string }) => {
      return Api.organization.updateFinancialIncentives(orgId, incentive);
    },
    {
      mutationKey: [...orgQueryKey(orgId), "financial-incentives"],
      onSuccess: (newOrg: OrganizationDto) => {
        qc.setQueryData(orgQueryKey(orgId), newOrg);
      },
    },
  );

  return mutation;
};

export const useOrganizationMailboxes = (
  organizationId: string,
): [OrganizationMailboxDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.organization.getOrganizationMailboxes(organizationId),
    [organizationId],
  );

  const {
    result: mailboxes,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["organizationMailboxes", organizationId],
    api: apiRequest,
    messages: { defaultError: RequestsError.GetOrg },
    dependencies: [organizationId],
  });

  return [mailboxes, request, refresh];
};

export const useOrganizationGetCompliantDone = (
  organizationId: string,
): [boolean, NetworkRequestState] => {
  const apiRequest = useCallback(
    () => Api.organization.setGetCompliantDone(organizationId, false),
    [organizationId],
  );

  const { result: done, request } = useDataRequest({
    queryKey: ["useOrganizationGetCompliantDone", organizationId, false],
    api: apiRequest,
    messages: {
      defaultError: RequestsError.GetOrg,
    },
    notReady: !organizationId,
  });

  return [done, request];
};

export const usePublicOrganizationNotices = (
  organizationId: OrganizationPublicId,
): [PublicOrganizationNoticeDto, NetworkRequestState, () => Promise<void>] => {
  const api = useCallback(
    () => Api.publicOrg.getOrganizationNotices(organizationId),
    [organizationId],
  );

  const { result, refresh, request } = useDataRequest({
    queryKey: ["publicOrganizationNotices", organizationId],
    api,
    notReady: !organizationId,
  });

  return [result, request, refresh];
};

export const usePublicDomainSetupInstructions = (
  organizationId: OrganizationPublicId,
): [PublicDomainSetupInstructionsDto, NetworkRequestState, () => Promise<void>] => {
  const api = useCallback(
    () => Api.publicOrg.getDomainSetupInstructions(organizationId),
    [organizationId],
  );

  const { result, refresh, request } = useDataRequest({
    queryKey: ["publicDomainSetupInstructions", organizationId],
    api,
    notReady: !organizationId,
  });

  return [result, request, refresh];
};

export const usePrimaryOrganization = (
  // If true, only return data from an API response, and don't return cached data
  onlyUseSettled = false,
): [Organization, NetworkRequestState, () => Promise<void>, InvalidateFn] => {
  const primaryOrgId = usePrimaryOrganizationId();

  return useOrganization(primaryOrgId, onlyUseSettled);
};

export const usePrimaryOrganizationId = (): string => {
  const [self] = useSelf();

  const activeOrgId =
    self &&
    self.activeOrganization &&
    self.organizations?.find((o) => o.organization == self.activeOrganization)?.organization;

  const selfOrgId = self?.organizations?.length > 0 ? self?.organizations?.[0].organization : null;

  return activeOrgId || selfOrgId;
};

//
// MCP access tokens
//
// These are the *current user's* tokens in this organization, not the organization's. The plaintext
// only ever comes back from `create`, so the caller has to hold on to it.
//
const mcpTokensQueryKey = (organizationId: OrganizationPublicId) => [
  "organizationMcpTokens",
  organizationId,
];

export const useOrganizationMcpTokens = (
  organizationId: OrganizationPublicId,
): [McpTokenDto[] | undefined, NetworkRequestState] => {
  const api = useCallback(() => Api.organization.listMcpTokens(organizationId), [organizationId]);

  const { result, request } = useDataRequest({
    queryKey: mcpTokensQueryKey(organizationId),
    api,
    notReady: !organizationId,
  });

  return [result, request];
};

export const useMcpTokenActions = (organizationId: OrganizationPublicId) => {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries(mcpTokensQueryKey(organizationId));
  };

  const createToken = useMutation(() => Api.organization.createMcpToken(organizationId), {
    onSuccess: invalidate,
  });

  const extendToken = useMutation(
    (tokenId: string) => Api.organization.extendMcpToken(organizationId, tokenId),
    { onSuccess: invalidate },
  );

  const revokeToken = useMutation(
    (tokenId: string) => Api.organization.revokeMcpToken(organizationId, tokenId),
    { onSuccess: invalidate },
  );

  return { createToken, extendToken, revokeToken };
};

export const useSelfOrganizationUser = (): OrganizationUserDto | undefined => {
  const [self] = useSelf();
  const primaryOrgId = usePrimaryOrganizationId();
  const [users] = useOrganizationUsers(primaryOrgId, true);
  return users?.find((u) => u?.email === self?.email);
};
